import React from 'react';
import { describeJobError } from '../../services/http';
import type { JobStatusResponse } from '../../services/dataJobs';
import { formatUtc } from '../../utils/dateWindow';
import { jobStatusBadge } from './jobStatusBadge';

export interface JobStatusPanelProps {
  jobId: string | null;
  /** The last status the API sent; null until the first one arrives. */
  status: JobStatusResponse | null;
  title?: string;
}

/** An ISO instant as '14 Oct 2025, 08:00 UTC'; '' when there is none or it cannot be read. */
const utcTime = (iso: string | null | undefined): string => (iso ? formatUtc(Date.parse(iso), 'hourly') : '');

const STEPS = ['Submitted', 'Processing', 'Completed'] as const;

/** How many of the three steps are done for a status. */
function stepsDone(status: string | undefined): number {
  switch (status) {
    case 'completed':
      return 3;
    case 'processing':
    case 'running':
    case 'failed':
      return 2;
    default:
      return 1;
  }
}

/** Where a running job has got to: its state, when it started and finished, and why it failed. */
const JobStatusPanel: React.FC<JobStatusPanelProps> = ({ jobId, status, title }) => {
  const state = status?.status;
  const badge = state ? jobStatusBadge(state) : { text: 'Submitted', className: 'badge-ghost' };
  const finished = state === 'completed' || state === 'failed';
  const done = stepsDone(state);
  const times: Array<[string, string]> = [
    ['Submitted', utcTime(status?.created_at)],
    ['Started', utcTime(status?.start_time)],
    ['Finished', utcTime(status?.end_time)],
  ];

  return (
    <div className="space-y-4 text-sm text-ink">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {title ? <span className="font-medium">{title}</span> : null}
          <span className={`badge badge-sm ${badge.className}`}>{badge.text}</span>
        </div>
        {jobId ? <span className="break-all font-mono text-xs text-ink-muted">Job ID: {jobId}</span> : null}
      </div>

      <ul className="steps w-full">
        {STEPS.map((label, i) => {
          const failedHere = state === 'failed' && i === 2;
          return (
            <li
              key={label}
              className={`step ${failedHere ? 'step-error' : i < done ? 'step-primary' : ''}`.trim()}
              data-content={failedHere ? '✕' : undefined}
            >
              {label}
            </li>
          );
        })}
      </ul>

      {finished ? null : (
        <progress className="progress progress-primary w-full" value={status?.progress ?? 0} max={100} aria-label="Job progress" />
      )}

      {times.some(([, value]) => value) ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-ink-muted">
          {times.map(([label, value]) =>
            value ? (
              <React.Fragment key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </React.Fragment>
            ) : null,
          )}
        </dl>
      ) : null}

      {state === 'failed' ? (
        <div className="alert alert-error">
          <div>
            <p className="font-bold">Job Failed</p>
            <p>{describeJobError(status?.error)}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default JobStatusPanel;
